import { useRef, useState } from 'react';
import type { AreaKind } from '../../domain/areaCatalog';
import type { V2Evidence } from '../../domain/evidence';
import type { FindingOrigin, V2Finding } from '../../domain/finding';
import { useT } from '../../i18n/i18n';
import { Button, SheetShell } from '../../ui/components';
import { FindingEditor, type FindingEditorHandle, type FindingEditorState, type FindingPrefill } from './FindingEditor';

// Sheet host of the finding editor for an area's "other finding" (a finding outside the checklist),
// for reviewing one from the Findings list, and for MODIFYING an AI suggestion before accepting it
// (Round 2 §Q: the AI text is only the starting content). Save Finding or Cancel always return the
// inspector to exactly where they were — never away from the area.
export function FindingEditorSheet(props: {
  inspectionId: string;
  areaId: string;
  areaKind: AreaKind;
  existing: V2Finding | null;
  prefill?: FindingPrefill;
  origin?: FindingOrigin;
  locked?: boolean;
  onClose: () => void;
  onSaved?: (findingId: string) => void;
  onOpenLightbox: (evidence: V2Evidence[], index: number, findingId: string | null) => void;
}) {
  const { t } = useT();
  const editor = useRef<FindingEditorHandle>(null);
  const [state, setState] = useState<FindingEditorState>({ canSave: false, dirty: false, draft: true });

  return (
    <SheetShell
      title={props.existing ? t('finding.editTitle') : t('finding.otherTitle')}
      onClose={() => editor.current?.cancel()}
      layer="over"
      testId="finding-editor-sheet"
      footer={
        <>
          <Button onClick={() => void editor.current?.save()} disabled={!state.canSave || props.locked === true} sub={t('finding.saveSub')} icon="save" testId="finding-save">
            {t('finding.save')}
          </Button>
          <Button variant="text" onClick={() => editor.current?.cancel()} testId="finding-cancel">
            {t('shell.cancel')}
          </Button>
        </>
      }
    >
      <FindingEditor
        ref={editor}
        inspectionId={props.inspectionId}
        areaId={props.areaId}
        areaKind={props.areaKind}
        existing={props.existing}
        prefill={props.prefill}
        origin={props.origin}
        locked={props.locked}
        onDone={props.onClose}
        onSaved={props.onSaved}
        onOpenLightbox={props.onOpenLightbox}
        onState={setState}
      />
    </SheetShell>
  );
}
